import React, { useEffect, useState, useRef } from 'react';
import { startLoadingSpinner, stopLoadingSpinner } from '../../csf-ufc-pivotal/includes/utils';
import PivotalFormApi from "../../../js/csf/pivotal/PivotalFormApi";
import PivotalSearchApi from "../../../js/csf/pivotal/PivotalSearchApi";
import PropTypes from "prop-types";
import { CsfPivotalFournisseurAddClient } from "./csf-fournisseur-recognition-request-add-client";
import JustValidate from 'just-validate';
import { findRemovedItems, updateHeadingBasedOnUrl } from '../includes/utils';
import CsfFournisseurRecognitionRenewalTitle
  from "../includes/csf-fournisseur-recognition-renewal-title";
import { CsfPivotalFournisseurAddRegion } from "./csf-fournisseur-recognition-request-add-region";

export function FournisseurRecognitionRequestStepTwoBlock({ hasPreviousRenewalEndDate, trainingId, currentStep, onStepChange, accessToken, apiUrl, apiEnv, form, updateForm, updateActivityCycleIdStep4 }) {

  const [clients, setClients] = useState([]);
  const [formationTypes, setFormationTypes] = useState([]);
  const [difficultyLevel, setDifficultyLevel] = useState('');
  const [selectedFormationType, setSelectedFormationType] = useState('');
  const [validator, setValidator] = useState(null);
  const [removeLocalStorage, setRemoveLocalStorage] = useState(false);
  const [isInscriptionFeeChecked, setIsInscriptionFeeChecked] = useState(null);
  const [originalClients, setOriginalClients] = useState([]);
  const langcode = document.documentElement.lang;
  const [clientBtns, setClientBtns] = useState([]);

  const [originalRegions, setOriginalRegions] = useState([]);
  const [regionBtns, setRegionBtns] = useState([]);
  const [regions, setRegions] = useState([]);

  const urlParams = new URLSearchParams(window.location.search);
  const isRenewal = (urlParams.get('renewal') === 'true' || hasPreviousRenewalEndDate);

  // isWebsiteChecked / setIsWebsiteChecked : Envoyé à l'IPF? set on step 1
  const [isSentToIPF, setIsSentToIPF] = useState(false);


  /*
  * On components mounts fetch the user access token only once.
  * */
  useEffect(() => {
    startLoadingSpinner();
  }, []);

  /*
  * delete local storage on form success
  * */
  useEffect(() => {
    if (removeLocalStorage === true) {
      localStorage.removeItem('form');
    }
  }, [removeLocalStorage]);

  /*
  * When the accessToken is fetched and state is updates
  * */
  useEffect(() => {

    if (accessToken) {
      getAllClients();
      getFormationTypes();
      getAllRegions();
    }
  }, [accessToken]);

  useEffect(() => {

    if (form?.Audience__Secondary !== undefined) {
      formatClientsOnLoad(form);
    }
    setDifficultyLevel(form?.Difficulty);
    setSelectedFormationType(form?.REDU_Activity_Type_Id);
    setIsInscriptionFeeChecked(form?.Inscription_Fee);
    setIsSentToIPF(form?.Submitted_To_Financial_Instit);

  }, [form]);

  /*
  * Function that filters clients received by the api on components load.
  * it make sure that we don't receive duplicated clients data and if so, filters and delete the extra ones
  * */
  function formatClientsOnLoad(form) {
    const { selectedAudiences, duplicateItems } = form.Audience__Secondary.reduce((acc, item) => {
      if (!acc.seen[item.Subject_Id]) {
        acc.seen[item.Subject_Id] = true;
        acc.selectedAudiences.push(item);
      } else {
        acc.duplicateItems.push(item);
      }
      return acc;
    }, { seen: {}, selectedAudiences: [], duplicateItems: [] });

    if (duplicateItems.length > 0) {
      deleteTargetAudience(duplicateItems);
    }

    // reset clientele list before setting the items, to prevent duplicates in list
    setClientBtns([]);
    addClientButtons(selectedAudiences);
    setOriginalClients(selectedAudiences);
  }

  function getAllClients() {
    const requestData = {
      search: 'CSF_EP_Customer_REST',
    };

    const pivotalSearchApi = new PivotalSearchApi(apiUrl, apiEnv, accessToken);
    pivotalSearchApi.callSearchEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
        if (response?.success && response?.payload?.data?.rows?.length >= 0) {
          const options = response.payload.data.rows;
          const formattedOptions = [];

          options.forEach(function (option) {
            formattedOptions.push({ label: langcode === 'fr' ? option.Name_French : option.Name_English, label_en: option.Name_English, value: option.REDU_Subject_Id });
          });

          setClients(formattedOptions);
          stopLoadingSpinner();
        }
      })
      .catch(error => {
        stopLoadingSpinner();
        console.error(error);
      });
  }

  function handleNextStepClick() {
    onStepChange(currentStep + 1); // Call the callback function with new count
  }

  function handlePreviousStepClick() {
    onStepChange(currentStep - 1); // Call the callback function with new count
  }

  function goToSpecificStep(step) {
    onStepChange(step); // Call the callback function with new count
  }

  const handleDifficultyChange = (event) => {
    setDifficultyLevel(event.target.value);
  };

  const handleSelectedFormationTypeChange = (event) => {
    setSelectedFormationType(event.target.value);
  };

  useEffect(() => {
    const validator = new JustValidate('#form-step02');

    validator
      .addField('#clientsValidationField', [
        {
          rule: 'required',
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required'),
        },
      ])
      .addField('#difficulty-level', [
        {
          rule: 'required',
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required'),
        },
      ])
      .addField('#formation-type', [
        {
          rule: 'required',
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required'),
        },
      ])
      .addField('#subscription-cost-money', [
        {
          rule: 'required',
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required'),
        },
        {
          rule: 'minNumber',
          value: 0,
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.integer'),
        },
      ])

    if (isInscriptionFeeChecked !== true) {
      validator.removeField('#subscription-cost-money');
    }

    if (isSentToIPF) {
      validator.addField('#regionsValidationField', [
        {
          rule: 'required',
          errorMessage: window.Drupal.t('fournisseur.creation_form.validation.required')
        },
      ]);
    }

    if (isRenewal) {
      validator.removeField('#difficulty-level');
      validator.removeField('#formation-type');
      validator.removeField('#clientsValidationField');
    }

    setValidator(validator);

  }, [isInscriptionFeeChecked, isSentToIPF]);

  function submitStep2(event) {
    event.preventDefault();
    const formElement = document.getElementById('form-step02');
    const data = new FormData(formElement);

    // loop through all clients and check if a client that was added is already in original clients to prevent from adding it twice.
    clientBtns.forEach(item => {
      if (item.REDU_Activity__Subject_Id === "") {
        const matchingItem = originalClients.find(obj => obj.Subject_Id === item.value);
        if (matchingItem) {
          item.REDU_Activity__Subject_Id = matchingItem.REDU_Activity__Subject_Id;
        }
      }
    });

    const clientsForSubmit = clientBtns.map((client) => ({
      REDU_Activity__Subject_Id: client.REDU_Activity__Subject_Id,
      Subject_Id: client.value,
    }));

    const removedItems = findRemovedItems(originalClients, clientsForSubmit, 'clients');
    if (removedItems.length > 0) {
      deleteTargetAudience(removedItems);
    }

    let regionsToStore = regionBtns.map((region) => ({
      REDU_Activity__Territory_Id: region.REDU_Activity__Territory_Id ? region.REDU_Activity__Territory_Id : [""],
      TM_Territory_Id: region.value,
      TM_Territory_Id__Rn_Descriptor: region.label,
    }));

    if (!(isSentToIPF)) {
      regionsToStore = [];
    }

    const removedRegionItems = findRemovedItems(originalRegions, regionsToStore, 'regions');
    if (removedRegionItems.length > 0) {
      deleteRegion(removedRegionItems);
    }

    const formStep2 = {
      For_Company_Only: data.get('offered-exclusively') === 'on',
      Inscription_Fee: data.get('inscription-fee') === 'on',
      Amount: data.get('subscription-cost-money'),
      Contact_For_CSF_WebSite: data.get('offered-to-larger-public') === 'on',
      Difficulty: difficultyLevel,
      REDU_Activity_Type_Id: selectedFormationType,
      Audience__Secondary: clientsForSubmit,
      Region__Secondary: regionsToStore,
    };

    const formData = {
      ...formStep2,
    };

    const requestData = {
      form: 'CSF_EP_UXRecognition_2_REST',
      deltaData: formData
    };

    if (trainingId !== '' && trainingId !== null) {
      requestData.recordId = trainingId;
    }

    const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);

    if (validator) {
      validator.validate();
      setTimeout(() => {
        if (validator?.isValid) {
          startLoadingSpinner();
          pivotalFormApi.callFormPutEndpoint(requestData, '_Espace_Partenaire')
            .then(response => {
              if (response?.success && response?.payload?.recordId != null) {
                setRemoveLocalStorage(true);
                updateActivityCycleIdStep4(response?.payload?.data?.primary?.Last_REDU_Cycle_Id);

                // force re-update the form (data for the request) from API
                updateForm(true);

                stopLoadingSpinner();
                console.log("%c Save step 2 successful", 'background: #5E5E5E; color: #0f0');
                setTimeout(() => {
                  stopLoadingSpinner();
                  handleNextStepClick();
                }, "100");
              }
            })
            .catch(error => {
              stopLoadingSpinner();
              console.error(error);
            });
        } else {
          console.error('Form validation failed!');
          document.getElementById("formClients").style.border = "none";
          if (document.getElementById("clientsValidationField").value.length === 0) {
            document.getElementById("formClients").style.border = "1px solid red";
          }

          document.getElementById("formRegions").style.border = "none";
          if (document.getElementById("regionsValidationField").value.length === 0 && document.getElementById("displayed-on-iqpf").checked) {
            document.getElementById("formRegions").style.border = "1px solid red";
          }
        }
      });
    }
  }

  /*
  * Method to delete the target audience in the step 2 of the form.
  * */
  function deleteTargetAudience(removedItems) {
    removedItems.forEach(item => {
      const requestData = {
        form: 'CSF_EP_Activity_Subject_Delete',
        recordId: item?.REDU_Activity__Subject_Id,
      };
      const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
      pivotalFormApi.callFormDeleteEndpoint(requestData, '_Espace_Partenaire')
        .then(response => {
        })
        .catch(error => {
          console.error(error);
        });
    });
  }

  function getFormationTypes() {
    const requestData = {
      search: 'CSF_EP_FormationType_REST'
    };

    const pivotalSearchApi = new PivotalSearchApi(apiUrl, apiEnv, accessToken);

    pivotalSearchApi.callSearchEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
        if (response?.success && response?.payload?.data?.rows?.length >= 0) {
          setFormationTypes(response.payload.data.rows);
          stopLoadingSpinner();
        }
      })
      .catch(error => {
        stopLoadingSpinner();
        console.error(error);
      });
  }

  const addClientButtons = (clients) => {
    setClientBtns((prevButtons) => [
      ...prevButtons,
      ...clients.map((client, index) => ({
        id: prevButtons.length + index + 1,
        REDU_Activity__Subject_Id: client.REDU_Activity__Subject_Id ? client.REDU_Activity__Subject_Id : "",
        value: client.Subject_Id,
        label: langcode === 'fr' ? client.Name_French : client.Name_English,
      }))
    ]);
  };

  const handleDelete = (value) => {
    setClientBtns(clientBtns.filter((button) => button.value !== value));
  };

  const inputRef = useRef(null);
  const handleFocus = () => {
    if (inputRef.current.value === "000.00") {
      inputRef.current.value = "";
    }
  };
  const handleBlur = () => {
    if (inputRef.current.value === "") {
      inputRef.current.value = "000.00";
    }
  };

  /*
  * Handle for modal submit of clientele
  * */
  const handleModalAdd = (pickedclients) => {
    const selectedClients = Array.from(pickedclients).map((checkbox) => ({
      id: checkbox.value,
      Subject_Id: checkbox.value,
      REDU_Activity__Subject_Id: checkbox.REDU_Activity__Subject_Id ? checkbox.REDU_Activity__Subject_Id : "",
      Name_French: checkbox.label,
      Name_English: checkbox.label_en,
    }));

    addClientButtons(selectedClients);
  };


  /*
  * Handle the selected chips to be added to the regions fields.
  * */
  function addRegionButtons(regions) {
    setRegionBtns((prevButtons) => [
      ...prevButtons,
      ...regions.map((region, index) => ({
        id: prevButtons.length + index + 1,
        REDU_Activity__Territory_Id: region.REDU_Activity__Territory_Id !== undefined ? region.REDU_Activity__Territory_Id : '',
        value: region.value,
        label: region.label,
      }))
    ]);
  }

  /*
  * Handle for regions modal submit.
  * */
  function handleModalAddRegion(pickedregions) {
    const selectedRegion = Array.from(pickedregions).map((checkbox, index) => ({
      id: index,
      value: checkbox.value,
      label: checkbox.label,
      REDU_Activity__Territory_Id: checkbox.REDU_Activity__Territory_Id !== undefined ? checkbox.REDU_Activity__Territory_Id : '',
    }));
    addRegionButtons(selectedRegion);

  }

  /*
  * Method that handle deleting the regions chips from the field.
  * */
  function handleDeleteRegion(id) {
    setRegionBtns(regionBtns.filter((button) => button.id !== id));
  }

  /*
    * Init the bootstrap tooltips on list change
    * */
  useEffect(() => {
    $('[data-toggle="tooltip"]').tooltip({
      customClass: 'sup-tooltip'
    });
    $('[data-toggle="tooltip-wide"]').tooltip({
      delay: { show: 0, hide: 2000 },
      customClass: 'wide-tooltip right-arrow'
    });
  });


  /*
  * When the form is fetched, generate the regions.
  * */
  useEffect(() => {

    if (form && form.Region__Secondary !== undefined && form.Region__Secondary.length > 0) {
      const formattedRegions = form?.Region__Secondary.map((region, index) => ({
        id: index,
        REDU_Activity__Territory_Id: region.REDU_Activity__Territory_Id,
        label: langcode === 'en' && region.En_territory_Name ? region.En_territory_Name : region.TM_Territory_Id__Rn_Descriptor,
        value: region.TM_Territory_Id,
        TM_Territory_Id: region.TM_Territory_Id,
      }))
        // trier Toutes en haut de liste retournee
        .sort((a, b) => {
          if (a.label.startsWith("Toutes") && !b.label.startsWith("Toutes")) {
            return -1;
          } else if (!a.label.startsWith("Toutes") && b.label.startsWith("Toutes")) {
            return 1;
          } else {
            return 0;
          }
        });

      setOriginalRegions(formattedRegions);
      // when source data (form) is updated, reset regions in the list.
      setRegionBtns([]);
      addRegionButtons(formattedRegions);
    }

  }, [form]);

  /*
    * Method that fetch all the regions that exist.
    * */
  function getAllRegions() {
    const requestData = {
      search: 'CSF_EP_Territory_REST',
    };

    const pivotalSearchApi = new PivotalSearchApi(apiUrl, apiEnv, accessToken);

    pivotalSearchApi.callSearchEndpoint(requestData, '_Espace_Partenaire')
      .then(response => {
        if (response?.success && response?.payload?.data?.rows?.length >= 0) {
          const options = response.payload.data.rows;
          const formattedOptions = [];
          options.forEach(function (option) {
            formattedOptions.push({ label: langcode === 'en' && option.En_territory_Name ? option.En_territory_Name : option.TM_Territory_Id__Rn_Descriptor, value: option.TM_Territory_Id });
          });
            // trier Toutes en haut de liste retournee
          formattedOptions.sort((a, b) => {
              if ((a.label.startsWith("Toutes") && !b.label.startsWith("Toutes")) || (a.label.startsWith("All") && !b.label.startsWith("All"))) {
                return -1;
              } else if ((!a.label.startsWith("Toutes") && b.label.startsWith("Toutes")) || (!a.label.startsWith("All") && b.label.startsWith("All"))) {
                return 1;
              } else {
                return 0;
              }
            });

          setRegions(formattedOptions);
          stopLoadingSpinner();
        }
      })
      .catch(error => {
        stopLoadingSpinner();
        console.error(error);
      });
  }

  /*
    * Method to delete the regions in the step 1 of the form.
    * */
  function deleteRegion(removedItems) {
    removedItems.forEach(item => {
      const requestData = {
        form: 'CSF_EP_Activity_Territory_Delete',
        recordId: item?.REDU_Activity__Territory_Id,
      };
      const pivotalFormApi = new PivotalFormApi(apiUrl, apiEnv, accessToken);
      pivotalFormApi.callFormDeleteEndpoint(requestData, '_Espace_Partenaire')
        .then(response => {
        })
        .catch(error => {
          console.error(error);
        });
    });
  }

  // Change page title H1 if renewal
  useEffect(() => {
    updateHeadingBasedOnUrl("step2", "renewal", window.Drupal.t('fournisseur.creation_form.page.title'), window.Drupal.t('fournisseur.creation_form.page.renew.title'), hasPreviousRenewalEndDate);
  }, [hasPreviousRenewalEndDate]);

  return (
    <main className="fournisseur recognition">

      <h1 id="step2">{window.Drupal.t('fournisseur.creation_form.page.title')}</h1>

      <CsfFournisseurRecognitionRenewalTitle
        isRenewal={isRenewal || hasPreviousRenewalEndDate}
        formData={form} />

      <div className="content">

        <aside className="form-steps-legend">
          <ol>
            <li className="completed"><button className="form-steps-legend-button btn-link" onClick={() => goToSpecificStep(1)}><span>{window.Drupal.t('fournisseur.creation_form.steps.step1')}</span></button></li>
            <li className="active"><span>{window.Drupal.t('fournisseur.creation_form.steps.step2')}</span></li>
            <li><span>{window.Drupal.t('fournisseur.creation_form.steps.step3')}</span></li>
            <li><span>{window.Drupal.t('fournisseur.creation_form.steps.step4')}</span></li>
            <li><span>{window.Drupal.t('fournisseur.creation_form.steps.step5')}</span></li>
            <li><span>{window.Drupal.t('fournisseur.creation_form.steps.step6')}</span></li>
          </ol>
        </aside>

        <div className="form-steps-content">
          <form onSubmit={submitStep2} id="form-step02">

            <fieldset>
              <legend
                className="visually-hidden">{window.Drupal.t('fournisseur.creation_form.page.title')}</legend>

              <div className="row align-items-center">
                <div className="col-12 col-xl-6">
                  <div className="form-group d-flex align-items-center">
                    <input type="checkbox" id="offered-exclusively"
                           name="offered-exclusively"
                           defaultChecked={form?.For_Company_Only}/>
                    <label
                      htmlFor="offered-exclusively">{window.Drupal.t('fournisseur.creation_form.form.fields.training.chk_exclusive_offer')}</label>
                  </div>
                </div>
                <div className="col-12 col-xl-6">
                  <div className="row align-items-center">
                    <div className="col-12 col-sm-6">
                      <div className="form-group d-flex align-items-center">
                        <input type="checkbox" id="inscription-fee"
                               name="inscription-fee"
                               checked={isInscriptionFeeChecked}
                               onChange={(event) => setIsInscriptionFeeChecked(event.target.checked)}/>
                        <label
                          htmlFor="inscription-fee">{window.Drupal.t('fournisseur.creation_form.form.fields.training.chk_cost')}</label>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6">
                      <div className="form-group"
                           style={{visibility: isInscriptionFeeChecked ? "visible" : "hidden"}}>
                        <label
                          htmlFor="subscription-cost-money">{window.Drupal.t('fournisseur.creation_form.form.fields.training.cost')} ($)*</label>
                        <input
                          type="number"
                          id="subscription-cost-money"
                          name="subscription-cost-money"
                          ref={inputRef}
                          defaultValue={form?.Amount ? form?.Amount : "000.00"}
                          onFocus={handleFocus}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-group d-flex align-items-center">
                <input type="checkbox" id="offered-to-larger-public"
                       name="offered-to-larger-public"
                       defaultChecked={form?.Contact_For_CSF_WebSite}/>
                <label
                  htmlFor="offered-to-larger-public">{window.Drupal.t('fournisseur.creation_form.form.fields.training.chk_contact_request')}</label>
              </div>

              <div className="row">
                <div className="col-12 col-xl-5">
                  <div className="form-group">
                    <label
                      htmlFor="difficulty-level">{window.Drupal.t('fournisseur.creation_form.form.fields.difficulty.sel_label')}*</label>
                    <select id="difficulty-level" name="difficulty-level"
                            value={difficultyLevel}
                            onChange={handleDifficultyChange}
                            disabled={isRenewal}>
                      <option value=""></option>
                      <option
                        value="0">{window.Drupal.t('fournisseur.creation_form.form.fields.difficulty.sel_beginner')}</option>
                      <option
                        value="1">{window.Drupal.t('fournisseur.creation_form.form.fields.difficulty.sel_intermediate')}</option>
                      <option
                        value="2">{window.Drupal.t('fournisseur.creation_form.form.fields.difficulty.sel_advanced')}</option>
                    </select>
                  </div>
                </div>
                <div className="col-11 col-xl-6">
                  <div className="form-group">
                    <label
                      htmlFor="formation-type">{window.Drupal.t('fournisseur.creation_form.form.fields.training_type.sel_label')}*</label>
                    <select id="formation-type" name="formation-type"
                            value={selectedFormationType || ''}
                            onChange={handleSelectedFormationTypeChange}
                            disabled={isRenewal}>
                      <option value=""></option>
                      {formationTypes.map((formationType) => ( //langcode
                        <option key={formationType.REDU_Activity_Type_Id}
                                value={formationType.REDU_Activity_Type_Id}>
                          {langcode === 'fr' ? (
                            formationType.EP_Name_French
                          ) : (
                            formationType.EP_Name_English
                          )}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <img className="file-side-tooltip yellow-tooltip" src="/themes/custom/tink/dist/assets/info-tooltip.svg"
                     title={window.Drupal.t('fournisseur.creation_form.form.formation.type')}
                     data-toggle="tooltip-wide" data-html="true" data-placement="left" alt="Information"/>
              </div>

              <div className="form-group">
                <small
                  className="fake-label">{window.Drupal.t('fournisseur.creation_form.form.fields.targetted_clients')}*</small>

                <div className={`button-container ${isRenewal && 'disabled'}`} id="formClients">
                  <input type="text" id="clientsValidationField"
                         value={clientBtns}
                         className="just-validate-hidden-field"
                         disabled={isRenewal}/>
                  {clientBtns.map((button) => (
                    <button type="button" key={button.value} name="clients"
                            className="button btn-outlined btn-region"
                            data-activity-subject-id={button.REDU_Activity__Subject_Id}
                            data-client={button.value}
                            onClick={() => handleDelete(button.value)}
                            disabled={isRenewal}>
                      <span>{button.label}</span>
                      {!isRenewal &&
                        <img src="/themes/custom/tink/dist/assets/icn-delete.svg"
                            alt="" className='btn-delete'/>
                      }
                    </button>
                  ))}
                  {!isRenewal &&
                  <button className="button btn-icon-only" type="button"
                          data-toggle="modal" data-target="#AddClient">
                    <img src="/themes/custom/tink/dist/assets/icn-add.svg"
                         alt=""/>
                  </button>}
                </div>

              </div>

              {isSentToIPF  && (
                <div className="form-group form-group-regions">
                  <small
                    className="fake-label">{window.Drupal.t('fournisseur.creation_form.form.fields.region')}*</small>

                  <div className="button-container" id="formRegions">
                    <input type="text" id="regionsValidationField"
                           defaultValue={regionBtns}
                           className="just-validate-hidden-field"/>
                    {regionBtns.map((button) => (
                      <button type="button" key={button.id} name="regions"
                              className="button btn-outlined btn-region"
                              data-activity-territory-id={button.REDU_Activity__Territory_Id}
                              data-region={button.TM_Territory_Id}
                              onClick={() => handleDeleteRegion(button.id)}>
                        <span>{button.label}</span>
                        <img
                          src="/themes/custom/tink/dist/assets/icn-delete.svg"
                          alt="" className='btn-delete'/>
                      </button>
                    ))}
                    <button className="button btn-icon-only" type="button"
                            data-toggle="modal" data-target="#AddRegion">
                      <img src="/themes/custom/tink/dist/assets/icn-add.svg"
                           alt=""/>
                    </button>
                  </div>
                </div>
              )}


              <div className="row action-buttons">
                <div className="col text-center">
                  <button className="button btn-outlined btn-larger" type="button"
                          onClick={() => handlePreviousStepClick()}>
                    {window.Drupal.t('fournisseur.previous.btn')}
                  </button>
                </div>
                <div className="col text-center">
                  <button className="button btn-larger" type="submit">
                    {window.Drupal.t('fournisseur.next.btn')}
                  </button>
                </div>
              </div>

            </fieldset>
          </form>
        </div>
      </div>

      <CsfPivotalFournisseurAddRegion regions={regions}
                                      currentRegions={regionBtns}
                                      handleModalAdd={handleModalAddRegion}/>

      <CsfPivotalFournisseurAddClient clients={clients}
                                      currentClients={clientBtns}
                                      handleModalAdd={handleModalAdd}/>
    </main>
  );
}

FournisseurRecognitionRequestStepTwoBlock.propTypes = {
  hasPreviousRenewalEndDate: PropTypes.bool,
  currentStep: PropTypes.number.isRequired,
  onStepChange: PropTypes.func,
  accessToken: PropTypes.string.isRequired,
  apiUrl: PropTypes.string.isRequired,
  apiEnv: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
  updateForm: PropTypes.func.isRequired,
  trainingId: PropTypes.string,
  updateActivityCycleIdStep4: PropTypes.func.isRequired,
}
